import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User } from 'firebase/auth'

export type StoredUserDetails = {
  displayName: string | null
  email: string | null
  phoneNumber: string | null
  photoURL: string | null
  uid: string | null
}

interface UserContextType {
  user: User | null
  userDetails: StoredUserDetails | null
  setUser: (user: User | null) => void
  updatePhoneNumber: (phoneNumber: string) => void
  logout: () => void
}

const parseStoredUser = (raw: string | null): StoredUserDetails | null => {
  if (!raw) return null
  try {
    const userData = JSON.parse(raw)
    if (!userData) return null
    return {
      displayName: userData.displayName || userData.name || null,
      email: userData.email || null,
      phoneNumber: userData.phoneNumber || userData.phone_number || null,
      photoURL: userData.photoURL || userData.photo_url || null,
      uid: userData.uid || null,
    }
  } catch {
    return null
  }
}

const mergeUserDetails = (
  existing: StoredUserDetails | null,
  incoming: Partial<StoredUserDetails>
): StoredUserDetails => {
  const isPhoneOnlySignIn = !incoming.email && !!existing?.email

  return {
    displayName: incoming.displayName ?? existing?.displayName ?? null,
    email: incoming.email ?? existing?.email ?? null,
    phoneNumber: incoming.phoneNumber ?? existing?.phoneNumber ?? null,
    photoURL: incoming.photoURL ?? existing?.photoURL ?? null,
    uid: isPhoneOnlySignIn
      ? existing?.uid ?? incoming.uid ?? null
      : incoming.uid ?? existing?.uid ?? null,
  }
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userDetails, setUserDetails] = useState<UserContextType['userDetails']>(null)

  const persistUserDetails = (details: StoredUserDetails) => {
    setUserDetails(details)
    localStorage.setItem('firebase_auth_user', JSON.stringify(details))
  }

  useEffect(() => {
    const stored = parseStoredUser(localStorage.getItem('firebase_auth_user'))
    if (stored) {
      setUserDetails(stored)
    }
  }, [])

  const handleSetUser = (newUser: User | null) => {
    setUser(newUser)
    if (newUser) {
      const existing = userDetails ?? parseStoredUser(localStorage.getItem('firebase_auth_user'))
      const details = mergeUserDetails(existing, {
        displayName: newUser.displayName,
        email: newUser.email,
        phoneNumber: newUser.phoneNumber,
        photoURL: newUser.photoURL,
        uid: newUser.uid,
      })
      persistUserDetails(details)
    } else {
      setUserDetails(null)
    }
  }

  const updatePhoneNumber = (phoneNumber: string) => {
    const existing = userDetails ?? parseStoredUser(localStorage.getItem('firebase_auth_user'))
    if (!existing) return
    persistUserDetails({ ...existing, phoneNumber })
  }

  const logout = () => {
    setUser(null)
    setUserDetails(null)
    localStorage.removeItem('firebase_auth_idToken')
    localStorage.removeItem('firebase_auth_user')
    window.location.href = '/'
  }

  return (
    <UserContext.Provider value={{ user, userDetails, setUser: handleSetUser, updatePhoneNumber, logout }}>
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

